import { useState } from "react";
import {
    LuCirclePause,
    LuCirclePlay,
    LuDownload,
    LuFastForward,
    LuTrash2,
} from "react-icons/lu";
import ConfirmationDialog from "../../../../../../shared/ConfirmationDialog";
import { isActionablePendingNotificationStatus, SendType } from "../../../constants";
import "./NotificationDetailsSheetBottomBar.scss";
import { useSingleNotificationActions } from "./useSingleNotificationActions";

const ACTION_ICON_SIZE = 18;

/**
 * Footer inside the notification details drawer: move/send/delete when status is exact `PENDING`;
 * Download CSV always.
 * @param {object} props
 * @param {string} props.sendType Selected send type (`automated` | `manual`) for Move to Manual / Move to Automated label.
 * @param {boolean} [props.actionsDisabled] When true, disables all buttons (e.g. while details `useQuery` is loading).
 * @param {string | null} props.notificationId Selected notification id for single-row actions.
 * @param {Record<string, unknown> | null} props.listRow Selected list row from the queue.
 * @param {Record<string, unknown> | null | undefined} props.details Loaded notification details (`GET /notifications/details`); required for CSV export.
 * @param {unknown} [props.status] Notification status from list row or details; actions show only for exact `PENDING`.
 * @param {string | null} props.queueFilter Active queue tab ({@link QueueFilter}).
 * @param {() => void} props.refreshNotificationsList Same as TopBar: resets list + count queries for current filters.
 * @param {() => void} props.onDeleteSuccess Called after a successful delete (e.g. close the drawer).
 * @returns {import('react').JSX.Element}
 */
const NotificationDetailsSheetBottomBar = ({
    sendType,
    actionsDisabled,
    notificationId,
    listRow,
    details,
    status,
    queueFilter,
    refreshNotificationsList,
    onDeleteSuccess,
}) => {
    const {
        moveSendTypeMutation,
        sendNowMutation,
        deleteMutation,
        onDownloadCsvFile,
    } = useSingleNotificationActions({
        sendType,
        notificationId,
        listRow,
        details,
        queueFilter,
        status: status ?? details?.status ?? listRow?.status,
        refreshNotificationsList,
        onDeleteSuccess,
    });

    const isAutomated = sendType === SendType.AUTOMATED;
    const showQueueActions = isActionablePendingNotificationStatus(status);

    const resolvedNotificationId = notificationId || listRow?.id;
    const hasNotificationId = Boolean(resolvedNotificationId);

    const disableQueueActions = actionsDisabled || !hasNotificationId;
    const isDownloadCsvFileDisabled = actionsDisabled || !details;

    const [confirmation, setConfirmation] = useState(null);

    const handleConfirmationOpenChange = (isOpen) => {
        if (!isOpen) {
            setConfirmation(null);
        }
    };

    const downloadCsvButton = (
        <button
            type="button"
            className="notification-details-sheet-bottom-bar__action"
            disabled={isDownloadCsvFileDisabled}
            onClick={onDownloadCsvFile}
        >
            Download CSV
            <LuDownload aria-hidden size={ACTION_ICON_SIZE} />
        </button>
    );

    return (
        <>
            <ConfirmationDialog
                open={confirmation !== null}
                onOpenChange={handleConfirmationOpenChange}
                title={confirmation?.title}
                body={confirmation?.body}
                confirmLabel={confirmation?.confirmLabel}
                confirmPendingLabel={confirmation?.confirmPendingLabel}
                onConfirm={confirmation?.onConfirm}
            />

            <div className="notification-details-sheet-bottom-bar">
                <div className="notification-details-sheet-bottom-bar__actions">
                    {showQueueActions && (
                        <>
                            <button
                                type="button"
                                className="notification-details-sheet-bottom-bar__action"
                                disabled={disableQueueActions}
                                onClick={() => {
                                    setConfirmation({
                                        title: isAutomated
                                            ? "Pause notification?"
                                            : "Automate notification?",
                                        body: isAutomated
                                            ? "This will pause the currently selected notification and move it to the Manual tab."
                                            : "This will resume sending the currently selected notification and move it to the Automated tab.",
                                        confirmLabel: isAutomated
                                            ? "Pause notification"
                                            : "Resume sending notification",
                                        confirmPendingLabel: isAutomated
                                            ? "Pausing notification"
                                            : "Resuming notification",
                                        onConfirm: () =>
                                            moveSendTypeMutation.mutateAsync(),
                                    });
                                }}
                            >
                                {isAutomated ? (
                                    <>
                                        Move to Manual
                                        <LuCirclePause
                                            aria-hidden
                                            size={ACTION_ICON_SIZE}
                                        />
                                    </>
                                ) : (
                                    <>
                                        Move to Automated
                                        <LuFastForward
                                            aria-hidden
                                            size={ACTION_ICON_SIZE}
                                        />
                                    </>
                                )}
                            </button>
                            <button
                                type="button"
                                className="notification-details-sheet-bottom-bar__action"
                                disabled={disableQueueActions}
                                onClick={() => {
                                    setConfirmation({
                                        title: "Send notification now?",
                                        body: "This will send the selected notification immediately.",
                                        confirmLabel:
                                            "Immediately send selected notification",
                                        confirmPendingLabel:
                                            "Sending notification",
                                        onConfirm: () =>
                                            sendNowMutation.mutateAsync(),
                                    });
                                }}
                            >
                                Send Now
                                <LuCirclePlay
                                    aria-hidden
                                    size={ACTION_ICON_SIZE}
                                />
                            </button>
                            <button
                                type="button"
                                className="notification-details-sheet-bottom-bar__action"
                                disabled={disableQueueActions}
                                onClick={() => {
                                    setConfirmation({
                                        title: "Delete notification?",
                                        body: "This will permanently delete the selected notification. This action cannot be undone.",
                                        confirmLabel:
                                            "Delete selected notification",
                                        confirmPendingLabel:
                                            "Deleting notification",
                                        onConfirm: () =>
                                            deleteMutation.mutateAsync(),
                                    });
                                }}
                            >
                                Delete
                                <LuTrash2 aria-hidden size={ACTION_ICON_SIZE} />
                            </button>
                        </>
                    )}
                    {downloadCsvButton}
                </div>
            </div>
        </>
    );
};

export default NotificationDetailsSheetBottomBar;
